import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatSelectionList } from '@angular/material/list';
import { BehaviorSubject, distinctUntilChanged, every, filter, map, skip, take, takeUntil } from 'rxjs';
import { IMember } from '../../models/member';
import { DistributeService } from '../../services/distribute.service';
import { BaseComponent } from '../base.component';

@Component({
  selector: 'app-team-members',
  templateUrl: './team-members.component.html',
  styleUrls: ['./team-members.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class TeamMembersComponent
  extends BaseComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() selectedMembers: IMember[] = [];

  selectedSourceList: IMember[] = [];
  _sourceSub = new BehaviorSubject<IMember[]>([]);
  source$ = this._sourceSub.asObservable();

  selectedTargetList: IMember[] = [];
  _targetSub = new BehaviorSubject<IMember[]>([]);
  target$ = this._targetSub.asObservable();

  @Output() onAdd = new EventEmitter<IMember[]>();
  @Output() onRemove = new EventEmitter<IMember[]>();
  @Output() targetChanges = new EventEmitter<IMember[]>();

  @ViewChild('sourceTeam', { static: false }) private sourceTeam: MatSelectionList;
  @ViewChild('targetTeam', { static: false }) private targetTeam: MatSelectionList;
  trackByFn(index: number, item: IMember) {
    return item?.id ?? index;
  }

  compareMembers(memberA: IMember, memberB: IMember) {
    return memberA && memberB ? memberA?.id === memberB?.id : memberA === memberB;
  }

  constructor(private distributeService: DistributeService) {
    super();
  }

  ngOnInit(): void {
    this._initMembersList();
    this._subscribeToTargetChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this._watchMembersChanges(changes);
  }

  add() {
    const data = this._targetSub.getValue();
    this.onAdd.emit(data.concat(this.selectedSourceList));
    this._addToTarget();
    this._removeFromSource();
  }

  remove() {
    this.onRemove.emit(this.selectedTargetList);
    this._addTosSource();
    this._removeFromTarget();
  }

  addAll() {
    this.sourceTeam.selectAll();
    this.add();
  }

  removeAll() {
    this.targetTeam.selectAll();
    this.remove();
  }

  private _watchMembersChanges(changes: SimpleChanges) {
    if (changes?.selectedMembers) {
      this._targetSub.next(changes?.selectedMembers?.currentValue);
    }
    if (!changes?.selectedMembers.firstChange) {
      this._initMembersList();
      this._resetSelected();
    }
  }

  private _subscribeToTargetChanges() {
    this.target$
      .pipe(
        takeUntil(this.destroy$),
        skip(1),
        distinctUntilChanged((prev, cur) => {
          return JSON.stringify(prev) === JSON.stringify(cur);
        })
      )
      .subscribe((res) => {
        this.targetChanges.emit(res);
      });
  }

  private _initMembersList() {
    this.distributeService
      .getMembersByVisitKey(this.visitInfo.key)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this._sourceSub.next(data.filter((itm) => this._checkIfNotSelected(itm.id)));
      });
  }

  private _removeFromSource() {
    const selectedItems = this.selectedSourceList.map((it) => it?.id);
    const value = this._sourceSub.getValue();
    const newData = value.filter((mbr) => !selectedItems.includes(mbr.id));
    this._sourceSub.next(newData);
    this.selectedSourceList = [];
  }

  private _addToTarget() {
    const value = this._targetSub.getValue();
    this._targetSub.next(value.concat(this.selectedSourceList));
  }

  private _removeFromTarget() {
    const selectedItems = this.selectedTargetList.map((it) => it?.id);
    const value = this._targetSub.getValue();
    const newData = value.filter((mbr) => !selectedItems.includes(mbr?.id));
    this._targetSub.next(newData);
    this.selectedTargetList = [];
  }

  private _addTosSource() {
    const value = this._sourceSub.getValue();
    this._sourceSub.next(value.concat(this.selectedTargetList));
  }

  private _checkIfNotSelected(id) {
    return this.selectedMembers?.every((itm) => itm.id !== id);
  }

  private _resetSelected() {
    this.selectedSourceList = [];
    this.selectedTargetList = [];
  }
}
